import { Portal, Project, Attachment } from '@/types'
import { apiPortals } from '@/routes'
import { RecordType } from '@/components/shared/DeleteRecordModal'
import apiSlice, { deleteRecordSuccess } from '@/features/apiSlice'
import { startAppListening } from '@/store/listenerMiddleware'

startAppListening({
  actionCreator: deleteRecordSuccess,
  async effect({ payload: { recordType, recordIdOrSlug, record } }, listenerApi) {
    if (recordType === RecordType.Portal) {
      listenerApi.dispatch(
        apiSliceWithPortals.util.updateQueryData('getPortals', undefined, (portals) => (
          portals.filter((portal) => portal.slug !== recordIdOrSlug)),
        ),
      )
    } else if (recordType === RecordType.Attachment) {
      const deletedAtt = record as unknown as Attachment
      if (deletedAtt.recordType !== RecordType.Portal) return

      const cacheKeys = apiSliceWithPortals.util.selectCachedArgsForQuery(listenerApi.getState(), 'getPortal') ?? []
      const portalSlug = cacheKeys
        .map(key => apiSliceWithPortals.endpoints.getPortal.select(key)(listenerApi.getState())?.data)
        .find(portal => portal?.id === deletedAtt.recordId)
        ?.slug

      listenerApi.dispatch(apiSliceWithPortals.util.updateQueryData('getPortal', portalSlug!, (portal) => {
        if (deletedAtt.name === 'photos') {
          portal[deletedAtt.name] = portal[deletedAtt.name]!.filter((att) => att.id !== deletedAtt.id)
        }
      }))
    }
  },
})

export const apiSliceWithPortals = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPortals: builder.query<Portal[], void>({
      query: () => apiPortals.index(),
    }),
    getPortal: builder.query<Portal, string>({
      query: (portalSlug) => apiPortals.show(portalSlug),
    }),
    getPortalProjects: builder.query<Project[], void>({
      query: () => apiPortals.projects(),
    }),
    createPortal: builder.mutation<Portal, FormData>({
      query: (body) => ({ ...apiPortals.create(), body }),
      async onQueryStarted(_body, lifecycleApi) {
        const { data: portal } = await lifecycleApi.queryFulfilled
        lifecycleApi.dispatch(
          apiSliceWithPortals.util.updateQueryData('getPortals', undefined, (draft) => { draft.unshift(portal) }),
        )
        lifecycleApi.dispatch(
          apiSliceWithPortals.util.updateQueryData('getPortal', portal.slug, () => portal),
        )
      },
    }),
    updatePortal: builder.mutation<Portal, { portalSlug: string, body: FormData }>({
      query: ({ portalSlug, body }) => ({ ...apiPortals.update(portalSlug), body }),
      async onQueryStarted({ portalSlug }, lifecycleApi) {
        const { data: portal } = await lifecycleApi.queryFulfilled
        lifecycleApi.dispatch(
          apiSliceWithPortals.util.updateQueryData('getPortal', portalSlug, () => portal),
        )
      },
    }),
    togglePublished: builder.mutation<Portal, { portalSlug: string, published: boolean }>({
      query: ({ portalSlug, published }) => ({
        ...apiPortals.togglePublished(portalSlug),
        body: { published },
      }),
      async onQueryStarted(_args, lifecycleApi) {
        const { data: portal } = await lifecycleApi.queryFulfilled
        lifecycleApi.dispatch(apiSliceWithPortals.util.updateQueryData('getPortal', portal.slug, (draft) => {
          draft.published = portal.published
          return draft
        }))
      },
    }),
  }),
})

export const {
  useGetPortalsQuery,
  useGetPortalQuery,
  useGetPortalProjectsQuery,
  useCreatePortalMutation,
  useUpdatePortalMutation,
  useTogglePublishedMutation,
} = apiSliceWithPortals
